import React from "react";
import { s } from "../styles";
import Flag from "./Flag";
import MoneyBox from "./MoneyBox";
import TextBox from "./TextBox";
import OtherRow from "./OtherRow";
import type { T4Employee, T4Employer } from "../types";

interface SlipProps {
  employer: T4Employer;
  employee: T4Employee;
  year: number;
}

export default function Slip({ employer, employee, year }: SlipProps) {
  const e = employee;
  return (
    <div style={s.slip}>
      <div style={s.vprot}>
        <span>Protected B when completed / Protégé B une fois rempli</span>
        <span style={{ fontWeight: "normal" }}>T4 (25)</span>
      </div>

      <div style={s.topband}>
        <div style={s.empbox}>
          <div style={s.cap}>Employer's name &ndash; Nom de l'employeur</div>
          <div style={s.empAddr}>
            {employer.name}
            <br />
            {employer.addr1}
            <br />
            {employer.addr2}&nbsp;&nbsp;&nbsp; {employer.prov} &nbsp;{" "}
            {employer.postal}
          </div>
        </div>
        <div style={s.cracell}>
          <div style={s.crarow1}>
            <Flag />
            <div style={s.craname}>
              <span style={s.cranameSpan}>Canada Revenue Agency</span>
              <span style={s.cranameSpan}>Agence du revenu du Canada</span>
            </div>
          </div>
          <div style={s.crarow2}>
            <span style={s.yrlbl}>
              Year
              <br />
              Année
            </span>
            <span style={s.yrbox}>{year}</span>
          </div>
        </div>
        <div style={s.t4title}>
          <div style={s.t4}>T4</div>
          <div style={s.st}>Statement of Remuneration Paid</div>
          <div style={{ fontSize: 10 }}>État de la rémunération payée</div>
        </div>
      </div>

      <div style={s.bodyrow}>
        <div style={s.leftcol}>
          <div style={s.box54}>
            <span style={s.box54n}>54</span>
            <div style={s.box54b}>
              <div style={s.box54l}>
                Employer's account number &ndash; Numéro de compte de l'employeur
              </div>
              <div style={s.box54v}>{employer.account}</div>
            </div>
          </div>
          <div style={s.lrow}>
            <TextBox
              flex={1.5}
              num="12"
              value={e.sin}
              label={
                <>
                  Social insurance number
                  <br />
                  Numéro d'assurance sociale
                </>
              }
            />
            <div style={{ ...s.mbox, flex: 1 }}>
              <div style={s.mlabel}>
                <b>Exempt &ndash; Exemption</b>
              </div>
              <div style={s.exf}>
                <span style={s.exfn}>28</span>
                <div style={s.excols}>
                  <div>
                    CPP/QPP
                    <div style={s.cbx} />
                    RPC/RRQ
                  </div>
                  <div>
                    EI
                    <div style={s.cbx} />
                    AE
                  </div>
                  <div>
                    PPIP
                    <div style={s.cbx} />
                    RPAP
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={s.empname}>
            <div style={s.cap}>
              Employee's name and address &ndash; Nom et adresse de l'employé
            </div>
            <div style={s.nhdr}>
              <span>
                Last name (in capital letters) &ndash; Nom de famille (en lettres moulées)
              </span>
              <span>First name &ndash; Prénom</span>
              <span>Initial &ndash; Initiale</span>
            </div>
            <div style={s.nameline}>
              <span style={s.arw}>&#9654;</span>
              <span style={s.ln}>{e.last}</span>
              <span style={s.fn}>{e.first}</span>
              <span style={s.inch}>{e.init}</span>
            </div>
            <div style={s.addr}>
              {e.addr1}
              <br />
              {e.addr2}&nbsp;&nbsp;&nbsp; {e.province} &nbsp; {e.postal}
            </div>
          </div>
        </div>

        <div style={s.midcol}>
          <TextBox
            num="45"
            value=""
            label={
              <>
                Employer-offered dental benefits
                <br />
                Prestations dentaires offertes par l'employeur
              </>
            }
          />
          <TextBox
            num="10"
            value={e.province}
            label={
              <>
                Province of employment
                <br />
                Province d'emploi
              </>
            }
          />
          <TextBox
            num="29"
            value=""
            label={
              <>
                Employment code
                <br />
                Code d'emploi
              </>
            }
          />
        </div>

        <div style={s.rightcol}>
          <div style={s.mrow}>
            <MoneyBox num="14" value={e.b14} label={<>Employment income &ndash; Revenus d'emploi</>} />
            <MoneyBox num="22" value={e.b22} label={<>Income tax deducted &ndash; Impôt sur le revenu retenu</>} />
          </div>
          <div style={s.mrow}>
            <MoneyBox num="16" value={e.b16} label={<>Employee's CPP contributions &ndash; see over<br />Cotisations de l'employé au RPC &ndash; voir au verso</>} />
            <MoneyBox num="17" value={e.b17} label={<>Employee's QPP contributions &ndash; see over<br />Cotisations de l'employé au RRQ &ndash; voir au verso</>} />
          </div>
          <div style={s.mrow}>
            <MoneyBox num="16A" value={e.b16A} label={<>Employee's second CPP contributions &ndash; see over<br />Deuxièmes cotisations de l'employé au RPC</>} />
            <MoneyBox num="17A" value={e.b17A} label={<>Employee's second QPP contributions &ndash; see over<br />Deuxièmes cotisations de l'employé au RRQ</>} />
          </div>
          <div style={s.mrow}>
            <MoneyBox num="24" value={e.b24} label={<>EI insurable earnings &ndash; Gains assurables d'AE</>} />
            <MoneyBox num="26" value={e.b26} label={<>CPP/QPP pensionable earnings<br />Gains ouvrant droit à pension &ndash; RPC/RRQ</>} />
          </div>
          <div style={s.mrow}>
            <MoneyBox num="18" value={e.b18} label={<>Employee's EI premiums &ndash; Cotisations de l'employé à l'AE</>} />
            <MoneyBox num="44" value={e.b44} label={<>Union dues &ndash; Cotisations syndicales</>} />
          </div>
          <div style={s.mrow}>
            <MoneyBox num="20" value={e.b20} label={<>RPP contributions &ndash; Cotisations à un RPA</>} />
            <MoneyBox num="46" value={e.b46} label={<>Charitable donations &ndash; Dons de bienfaisance</>} />
          </div>
          <div style={s.mrow}>
            <MoneyBox num="52" value={e.b52} label={<>Pension adjustment &ndash; Facteur d'équivalence</>} />
            <MoneyBox num="50" value={e.b50} label={<>RPP or DPSP registration number<br />N° d'agrément d'un RPA ou d'un RPDB</>} />
          </div>
          <div style={s.mrow}>
            <MoneyBox num="55" value={e.b55} label={<>Employee's PPIP premiums &ndash; see over<br />Cotisations de l'employé au RPAP &ndash; voir au verso</>} />
            <MoneyBox num="56" value={e.b56} label={<>PPIP insurable earnings &ndash; Gains assurables du RPAP</>} />
          </div>
        </div>
      </div>

      <div style={s.other}>
        <div style={{ fontSize: 7 }}>
          Other information (see over) &ndash; Autres renseignements (voir au verso)
        </div>
        <OtherRow />
        <OtherRow />
      </div>
      <div style={s.sfoot}>
        <span>
          <b>T4 (25)</b>
        </span>
        <span style={{ fontFamily: "'Courier New', monospace" }}>
          REV &nbsp; OSP
        </span>
      </div>
    </div>
  );
}